import { useState, type FormEvent, type ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Upload, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ApiCallError } from '@/lib/api';

interface Props {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadInvoiceDialog({ tenantId, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('no file');
      const fd = new FormData();
      fd.append('tenantId', tenantId);
      fd.append('file', file);
      const res = await fetch('/api/admin/invoices/upload', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: 'unknown' }))) as { error: string };
        throw new ApiCallError(res.status, body);
      }
      return res.json() as Promise<{ file: { id: string }; extractionJobId: string }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['review-queue'] });
      onOpenChange(false);
      setFile(null);
      setError(null);
      navigate('/admin/review-queue');
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.body.error === 'unsupported_mime') {
          const mime = (err.body as unknown as { mime?: string }).mime;
          setError(`Tipo de archivo no soportado${mime ? `: ${mime}` : ''}`);
        } else if (err.body.error === 'missing_file') {
          setError('Seleccioná un archivo');
        } else {
          setError(err.body.error);
        }
      } else {
        setError('No se pudo subir el archivo');
      }
    },
  });

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Seleccioná un archivo');
      return;
    }
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Subir factura manualmente</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Subí una foto o PDF de factura. La IA va a intentar extraer los datos y la factura
            aparecerá en la cola de revisión.
          </p>

          <div className="space-y-1.5">
            <Label required>Archivo (jpg, png, webp, heic, pdf — máx 15 MB)</Label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
              onChange={onPick}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-border"
            />
          </div>

          {file && (
            <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
              <FileText className="size-4 text-primary" />
              <div>
                <div className="font-medium text-foreground">{file.name}</div>
                <div>
                  {(file.size / 1024).toFixed(1)} KB · {file.type}
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !file}>
              <Upload />
              {mutation.isPending ? 'Subiendo…' : 'Subir y procesar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
